import { Link, useNavigate } from "react-router-dom";
import { AED, deliveryFeeForBox, useDeliveryFees } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ImageIcon } from "lucide-react";

const imgSrc = (s) => (!s ? "" : s.startsWith("http") || s.startsWith("data:") || s.startsWith("/") ? s : `data:image/jpeg;base64,${s}`);
const ROYAL = "#3B63E0";

export default function ListingCard({ listing, index = 0, compact = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const thumb = imgSrc(listing.ai_thumbnail || (listing.seller_photos && listing.seller_photos[0]));
  const isOwner = user && listing.seller_id === user.id;
  const isFree = listing.listing_type === "free";
  const feeConfig = useDeliveryFees();
  const freeFee = listing.delivery_fee || deliveryFeeForBox(listing.preferred_box_size, feeConfig);

  const goOffer = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isFree) { navigate(`/listing/${listing.id}?claim=1`); return; }
    navigate(isOwner ? `/listing/${listing.id}` : `/listing/${listing.id}?offer=1`);
  };

  return (
    <Link
      to={`/listing/${listing.id}${isFree ? "?claim=1" : ""}`}
      data-testid={`listing-card-${listing.id}`}
      className="group fade-up flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
      style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        {thumb ? (
          <img src={thumb} alt={listing.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full items-center justify-center text-slate-300"><ImageIcon className={compact ? "h-7 w-7" : "h-9 w-9"} /></div>
        )}
        {isFree && (
          <span data-testid="free-badge" className={`absolute left-2 top-2 rounded-full font-bold text-white ${compact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs"}`} style={{ backgroundColor: ROYAL }}>FREE</span>
        )}
      </div>
      <div className={`flex flex-1 flex-col ${compact ? "p-2" : "p-3"}`}>
        <h3 className={`line-clamp-2 font-medium leading-snug text-slate-900 ${compact ? "h-[2.1rem] text-xs" : "h-[2.6rem] text-sm"}`} data-testid="card-title">{listing.title}</h3>
        {isFree ? (
          <>
            <p className={`font-heading font-bold text-slate-900 ${compact ? "mt-1 text-base" : "mt-1.5 text-lg"}`} data-testid="card-free-price">FREE</p>
            <p className="text-xs text-slate-600" data-testid="card-free-delivery">{freeFee ? `Just pay ${AED(freeFee)} delivery` : "Just pay delivery"}</p>
          </>
        ) : (
          <>
            <p className={`font-heading font-bold text-slate-900 ${compact ? "mt-1 text-base" : "mt-1.5 text-lg"}`} data-testid="card-price">{AED(listing.listing_price)}</p>
            <p className="text-xs text-slate-400" data-testid="card-delivery">Incl. Delivery</p>
          </>
        )}
        <button
          type="button"
          onClick={goOffer}
          data-testid={isOwner ? `manage-listing-card-${listing.id}` : isFree ? `claim-item-card-${listing.id}` : `make-offer-card-${listing.id}`}
          className={`mt-auto flex h-[34px] w-full items-center justify-center rounded-full font-semibold transition-colors ${isOwner ? "border-2 bg-white hover:bg-slate-50" : "text-white hover:opacity-90"} ${compact ? "text-xs" : "text-sm"}`}
          style={isOwner ? { borderColor: ROYAL, color: ROYAL, marginTop: compact ? 8 : 12 } : { backgroundColor: ROYAL, marginTop: compact ? 8 : 12 }}
        >
          {isOwner ? "Manage Listing" : isFree ? "Claim this item" : "Make Offer"}
        </button>
      </div>
    </Link>
  );
}
